/**
 * Lobby character-stage tuning — see render/character-stage.ts.
 *
 * Kept in config/ beside `lighting-tuning.ts` for the same reason that file
 * exists: the dungeon was once too dark because its lighting was scattered
 * across three modules with no single place to read the current values. The
 * stage has its own rig (it is a lit display case, not a room) so it gets its
 * own table rather than borrowing the dungeon's.
 *
 * The rig was originally matched to a painterly backdrop *plate*. Since
 * 2026-08-25 the stage builds a real set out of the game's own dungeon models
 * (`render/stage-set.ts`), so the rig now matches the **dungeon's** own
 * lighting instead: the key/fill/ambient echo `lighting-tuning.ts`, and the two
 * sconces are real point lights at the torches rather than a gradient painted
 * into a JPEG. Tuned against the real `knight.glb`.
 */

/**
 * All four values dropped by roughly two thirds on 2026-08-25, when the
 * painterly backdrop plate became a real geometry set.
 *
 * They were not wrong before: a plate supplies its own darkness, so the rig
 * only had to light the figure standing in front of it, and it could be
 * generous. Pointed at actual stone the same numbers blew the walls out to
 * sandy cream — bright, flat, and nothing like the dungeon the player walks
 * into. The set is lit like a dungeon now: dim ambient, a soft key, and most of
 * the light coming from the two torches that are visibly on the wall.
 *
 * Deliberately lower than `lighting-tuning.ts`'s own AMBIENT/DIRECTIONAL (1.7
 * each) because the dungeon runs those through a post-processing grade and
 * vignette that darken the result. The stage has no post chain, so matching the
 * raw numbers would not match the look.
 */

/** Cool ambient, so unlit sides stay violet rather than going flat grey. */
export const STAGE_AMBIENT_COLOR = 0x8a8aaa;
export const STAGE_AMBIENT_INTENSITY = 0.42;

/** Candle-warm key from the left, and the only shadow-caster. */
export const STAGE_KEY_COLOR = 0xffcc6a;
export const STAGE_KEY_INTENSITY = 1.05;

/** Cool fill, so the shadow side separates from the background instead of merging. */
export const STAGE_FILL_COLOR = 0x9fc9ff;
export const STAGE_FILL_INTENSITY = 0.3;

/** Low warm bounce off the floor. Weak on purpose — it is a hint, not a light. */
export const STAGE_BOUNCE_COLOR = 0xffb800;
export const STAGE_BOUNCE_INTENSITY = 0.18;

/**
 * A long lens. Wide-angle on a figure this close exaggerates the KayKit
 * proportions into caricature — the head balloons and the boots shrink.
 */
export const STAGE_FOV_DEG = 28;

/**
 * How much of the viewport height the model fills. Leaves headroom above the
 * helm and floor below the boots, so the figure sits *in* the niche rather
 * than being cropped by it.
 */
export const STAGE_FILL_FRACTION = 0.68;

/**
 * Where on the model the camera aims, as a fraction of its height from the
 * feet.
 *
 * Was "bounding-box centre plus a nudge", which is right for a figure floating
 * on a plate and wrong for one standing in a room: aiming at the middle of the
 * body puts the horizon behind the chest and hides the floor the character is
 * standing on. Aiming a little below centre tips the floor into the bottom of
 * frame, which is what actually communicates contact with the ground.
 */
export const STAGE_AIM_HEIGHT_FRACTION = 0.44;

/**
 * How far above its aim point the camera sits, giving a gentle downward tilt.
 *
 * Small on purpose. A steeper angle shows more floor but starts to look like a
 * strategy-game view of a doll, and it foreshortens the figure — the opposite
 * of what a "your delver, standing there" panel is for.
 */
export const STAGE_CAMERA_LIFT_M = 0.45;

/** Off-axis fraction of the camera distance, so the figure reads as three-dimensional rather than as a flat elevation. */
export const STAGE_YAW_OFFSET_FRACTION = 0.13;

/**
 * The torch sconces. Warm, and much weaker than the dungeon's own
 * `BRAZIER_INTENSITY` (22 over 14m): this is a 12-unit alcove, not a hall, and
 * the same value here blows the stone out to white.
 */
export const STAGE_TORCH_COLOR = 0xffb055;
export const STAGE_TORCH_INTENSITY = 4.2;
export const STAGE_TORCH_RANGE_M = 7.5;

/**
 * Flicker depth and rate for the sconces.
 *
 * A real light flicker, replacing the CSS `animate-flicker` opacity pulse that
 * used to fade the whole panel: fading a rendered image dims the character
 * along with the torch, which is not what a guttering flame does. Two
 * incommensurable frequencies per torch so the two never beat in sync and the
 * pattern never obviously loops.
 */
export const STAGE_TORCH_FLICKER_DEPTH = 0.12;
export const STAGE_TORCH_FLICKER_HZ: readonly [number, number] = [2.7, 4.3];

/** One shadow-casting light, one small map. Enough to plant the feet; not enough to cost a frame. */
export const STAGE_SHADOW_MAP_SIZE = 1024;
/** Half-extent of the shadow camera, in units. Tight around the figure so the map's resolution is spent on it. */
export const STAGE_SHADOW_EXTENT_M = 3.2;
export const STAGE_SHADOW_BIAS = -0.0015;

/** Matches the dungeon's own background so any gap at the frame edge reads as gloom, not as a hole. */
export const STAGE_BACKGROUND_COLOR = 0x241d19;

/**
 * The palette the set is textured with — the Cellars recolor
 * (`config/stratum-palette.ts`), i.e. the floor the player is about to enter.
 *
 * Not the untextured default: the dungeon never shows the pack's stock atlas,
 * so using it here would make the lobby the one place in the game with a
 * different-looking stone.
 */
export const STAGE_PALETTE_STRATUM = 1;

/** Matches post-processing's own exposure so the stage is not brighter than the game. */
export const STAGE_EXPOSURE = 1.15;

/**
 * Retina is worth it on a figure this small; 3x+ is not. Capped rather than
 * taken from the device, since a 3x phone would otherwise render 9x the pixels
 * of a 1x desktop for a panel a third the size.
 */
export const STAGE_MAX_PIXEL_RATIO = 2;
